/* eslint-disable react/display-name */
import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react'
import Select, { Option } from 'rc-select'
import 'rc-select/assets/index.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { compareItem } from '@/Components/DisplayItems/Utils/FilterSelect'

interface UserItem {
    id: string | number
    [key: string]: any
}

interface UserFieldEditProps {
    selected?: UserItem[] | UserItem | null
    options?: UserItem[]
    isSingleSelect?: boolean
    userLoading?: boolean
    errors?: string
    item: { id: string }
    kedo: { t: (key: string) => string }
    userDisplay: (item: UserItem) => string
    handleSearch?: (value: string) => void
    getOptions?: (
        input: string,
        callback: (options: UserItem[]) => void
    ) => void
    addSelectedItem?: (items: UserItem[]) => void
}

interface UserFieldEditRef {
    focus: () => void
    blur: () => void
}

export const UserFieldEdit = forwardRef<UserFieldEditRef, UserFieldEditProps>(
    (
        {
            selected = [],
            options = [],
            isSingleSelect = false,
            userLoading = false,
            errors,
            item,
            kedo,
            userDisplay,
            handleSearch,
            getOptions,
            addSelectedItem,
        },
        ref
    ) => {
        const innerRef = useRef<any>(null)

        useImperativeHandle(ref, () => ({
            focus: () => {
                innerRef.current?.focus?.()
            },
            blur: () => {
                innerRef.current?.blur?.()
            },
        }))

        const [currentSelected, setCurrentSelected] = useState<UserItem[]>(
            Array.isArray(selected) ? selected : selected ? [selected] : []
        )

        useEffect(() => {
            setCurrentSelected(
                Array.isArray(selected) ? selected : selected ? [selected] : []
            )
        }, [selected])

        const filteredOptions = options.filter(
            (opt) => !currentSelected.some((sel) => sel.id === opt.id)
        )

        const filterOption = (input: string, option: any) => {
            const labelText = compareItem(option.props.children)
            return labelText.toLowerCase().includes(input.toLowerCase())
        }

        const handleChange = (selectedItems: any) => {
            const selectedArray = isSingleSelect
                ? selectedItems
                    ? [selectedItems]
                    : []
                : selectedItems || []

            const items = selectedArray.map((sel: any) => ({
                id: String(sel.value),
                value: String(sel.value),
                label: sel.label,
            }))

            setCurrentSelected(items)
            addSelectedItem?.(items)
        }

        const handleClick = () => {
            if (options.length === 0) {
                getOptions?.('', () => {
                    //Add here
                })
            }
        }

        return (
            <div>
                <Select
                    ref={innerRef}
                    value={currentSelected.map((it) => ({
                        value: it.id,
                        label: userDisplay(it),
                    }))}
                    className={`w-100 custom-select-react ${
                        errors ? 'border-red' : ''
                    }`}
                    id={`${item.id}-search`}
                    allowClear
                    showSearch
                    loading={userLoading}
                    onSearch={handleSearch}
                    placeholder={`${kedo.t('Search')}...`}
                    filterOption={filterOption}
                    suffixIcon={
                        <FontAwesomeIcon
                            spin={userLoading}
                            icon={userLoading ? faSpinner : faChevronDown}
                        />
                    }
                    notFoundContent={kedo.t('No items found')}
                    labelInValue
                    mode={!isSingleSelect ? 'multiple' : undefined}
                    onChange={handleChange}
                    onClick={handleClick}
                >
                    {filteredOptions.map((option) => (
                        <Option key={option.id} value={option.id}>
                            {userDisplay(option)}
                        </Option>
                    ))}
                </Select>
            </div>
        )
    }
)
